import { ArrowUpRight, Code2 } from "lucide-react";

/** One paper: where to read it, and where the code is. */
export interface Paper {
  title: string;
  authors: string;
  venue: string;
  href: string;
  /** The official repository or project page, when there is one. */
  code?: string;
}

/**
 * Papers to start from, one direction at a time. Each row is the paper, then two fixed-width
 * icon slots -- read, code -- so the icons line up down the list whatever the title's length;
 * a paper without code keeps its slot empty. Everything opens in a new tab.
 */
export function Reading({ papers }: { papers: Paper[] }) {
  const slot = "inline-flex size-8 items-center justify-center rounded-md transition-colors";
  return (
    <ul className="bg-card min-w-0 divide-y rounded-lg border">
      {papers.map((paper) => (
        <li key={paper.href} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-4 py-2.5">
          <a href={paper.href} target="_blank" rel="noreferrer" className="group min-w-0">
            <p className="text-sm font-medium group-hover:underline">{paper.title}</p>
            <p className="text-muted-foreground mt-0.5 text-xs">
              {paper.authors} · {paper.venue}
            </p>
          </a>
          <span className="flex items-center gap-0.5">
            <a
              href={paper.href}
              target="_blank"
              rel="noreferrer"
              aria-label={`Read ${paper.title}`}
              title="Paper"
              className={`${slot} text-muted-foreground hover:text-foreground hover:bg-muted`}
            >
              <ArrowUpRight className="size-4" aria-hidden />
            </a>
            {paper.code ? (
              <a
                href={paper.code}
                target="_blank"
                rel="noreferrer"
                aria-label={`Code for ${paper.title}`}
                title="Code"
                className={`${slot} text-muted-foreground hover:text-foreground hover:bg-muted`}
              >
                <Code2 className="size-4" aria-hidden />
              </a>
            ) : (
              <span className={slot} aria-hidden />
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}
